import React, { useEffect, useRef, useMemo } from 'react';
import { Card, Typography } from 'antd';
import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';

// Register the Treemap and DataLabels plugins
Chart.register(TreemapController, TreemapElement, ChartDataLabels);

const { Title } = Typography;

const defaultData = [
  { name: 'Category01', value: 92602, color: '#dc3545' },
  { name: 'Category02', value: 53954, color: '#28a745' },
  { name: 'Category03', value: 42500, color: '#ffc107' },
  { name: 'Category04', value: 95269, color: '#6f42c1' },
  { name: 'Category05', value: 31617, color: '#17a2b8' }
];

// Utility to lighten a hex color by a given percent (0-100)
function lightenColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 0xFF;
  const g = (num >> 8) & 0xFF;
  const b = num & 0xFF;
  const mix = channel => Math.round(channel + (255 - channel) * (percent / 100));
  return `#${((1 << 24) + (mix(r) << 16) + (mix(g) << 8) + mix(b))
    .toString(16)
    .slice(1)}`;
}

const CategoryChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Process the data from the backend or use default sample data
  const processedData = useMemo(() => {
    // If no data is provided or it's not an array, return the default data
    if (!data || !Array.isArray(data) || data.length === 0) {
      return defaultData;
    }
    
    try {
      // Map the backend data to the format expected by the component
      const colors = ['#dc3545', '#28a745', '#ffc107', '#6f42c1', '#17a2b8', '#fd7e14', '#20c997', '#e83e8c', '#6610f2', '#007bff'];
      
      const processedData = data.map((item, index) => ({
        name: item.name || `Category${index + 1}`,
        value: item.value || 0,
        color: colors[index % colors.length] // Cycle through colors
      }));
      
      return processedData;
    } catch (error) {
      console.error('Error processing category data:', error);
      return defaultData;
    }
  }, [data]);

  const treeData = useMemo(
    () => processedData.map(item => ({
      value: item.value,
      label: item.name,
      backgroundColor: lightenColor(item.color, 40), // lighter shade
      borderColor: item.color // original color border
    })),
    [processedData]
  );

  useEffect(() => {
    const ctx = chartRef.current.getContext('2d');
    if (chartInstance.current) chartInstance.current.destroy();

    chartInstance.current = new Chart(ctx, {
      type: 'treemap',
      data: {
        datasets: [
          {
            tree: treeData,
            key: 'value',
            spacing: 4,
            backgroundColor: ctx => treeData[ctx.dataIndex]?.backgroundColor,
            borderWidth: 2,
            borderColor: ctx => treeData[ctx.dataIndex]?.borderColor
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        scales: { x: { display: false }, y: { display: false } },
        plugins: {
          datalabels: {
            display: true,
            formatter: (value, context) => {
              const node = context.dataset.tree[context.dataIndex];
              return [`${node.label}`, `$${node.value.toLocaleString()}`];
            },
            color: '#000',
            font: { size: 12, weight: 'bold' },
            anchor: 'center',
            align: 'center'
          },
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const node = treeData[ctx.dataIndex];
                return `${node.label}: $${node.value.toLocaleString()}`;
              }
            }
          }
        }
      }
    });

    return () => chartInstance.current.destroy();
  }, [treeData]);

  return (
    <Card
      headStyle={{ padding: '12px 16px' }}
      bodyStyle={{ height: 300, padding: 0 }}
      className="dashboard-card"
      title={<Title level={4} style={{ margin: 0 }}>CATEGORY</Title>}
    >
      <div style={{ height: '100%', padding: '12px' }}>
        <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />
      </div>
    </Card>
  );
};

export default CategoryChart;
